// Menopause Score — daily composite (0–100) + 30d series + insight engine.
//
// This module is the single source of truth for Score math. Both the home
// card, detail screen, Symptom Intelligence Report PDF, and Doctor Portal trend tab call
// the helpers below — never recompute the band cutoffs or weights anywhere
// else. If the formula moves, it moves here.

import {
  fetchLogsBetween,
  fetchHotFlashEventsBetween,
  localDateKey,
  parseLocalDateKey,
  type DailyLogEntry,
  type SymptomEvent,
} from "./dailyLogs";
import { supabase } from "@/integrations/supabase/client";

// 18 tracked symptoms — the full Daily Check-in symptom list (symList in
// KnowMenoApp.tsx) minus the "Feeling okay today!" sentinel. Hot flashes
// uses event-count bucketing (see hotFlashSeverityBucket); the other 17
// use the daily_logs 1..4 severity scale directly.
export const TRACKED_SYMPTOMS = [
  "Hot flashes",
  "Night sweats",
  "Brain fog",
  "Mood swings",
  "Depression",
  "Irritability",
  "Anxiety",
  "Joint pain",
  "Fatigue",
  "Poor sleep",
  "Frequent urination",
  "Low libido",
  "Vaginal dryness",
  "Heart palpitations",
  "Headaches",
  "Weight changes",
  "Hair thinning",
  "Memory issues",
] as const;
export type TrackedSymptom = (typeof TRACKED_SYMPTOMS)[number];

// v2.0 scoring constants.
//
// SEVERITY_POINTS — intensity-weighted curve so a severe-4 symptom counts
// roughly six times a mild-1 symptom (not four times, as a linear scale
// would imply). Values are calibrated against the spec test vector.
export const SEVERITY_POINTS = [0, 1, 2, 4, 6] as const; // index = clamped severity 0..4
export function severityToPoints(sev: number | null | undefined): number {
  if (typeof sev !== "number" || !Number.isFinite(sev) || sev <= 0) return 0;
  const clamped = Math.min(4, Math.max(1, Math.round(sev)));
  return SEVERITY_POINTS[clamped];
}

// REFERENCE_BURDEN = 40 points. This is the "reference-severe day"
// denominator — roughly 6–7 simultaneously-severe symptoms (e.g. 6×sev4 =
// 36, 7×sev4 = 42). It is NOT the theoretical maximum (10×sev4 = 60 pts,
// which clamps to a score of 0). Picking a reference instead of the
// theoretical max means a realistically bad day already reads near 0,
// instead of compressing all real-world variance into the top half of the
// scale.
export const REFERENCE_BURDEN = 40;

// Methodology disclosure — single source of truth, shown verbatim on the
// score detail page and inside the Symptom Intelligence Report PDF.
export const METHODOLOGY_DISCLOSURE =
  "Symptom data based on patient self-report logged daily. Score v2.0: symptom load scored against a reference-severe day with intensity-weighted severities; higher scores indicate lower symptom burden.";

// Score v2.0 release date — used to auto-expire the "we updated your score"
// change note shown on the card and detail screen.
export const SCORE_V2_RELEASE_DATE = "2026-06-12";
export const SCORE_V2_NOTE_EXPIRES_DAYS = 14;
export const SCORE_V2_CHANGE_NOTE =
  "We've improved how your score reflects symptom intensity — your history has been updated too, so your trends still compare apples to apples.";

// Map a per-day hot flash EVENT COUNT to the 1..4 severity scale so the
// composite can treat all symptoms uniformly. 0 events = no contribution
// (NULL, not 0). Buckets unchanged from v1.
export function hotFlashSeverityBucket(eventCount: number): number | null {
  if (!Number.isFinite(eventCount) || eventCount <= 0) return null;
  if (eventCount < 4) return 1;   // mild  (1–3 events)
  if (eventCount < 10) return 2;  // moderate (4–9)
  if (eventCount < 20) return 3;  // severe (10–19)
  return 4;                       // very severe (20+)
}

// Vitality Score band table (kept untouched — VitalityScoreCard,
// VitalityScoreDetail, and vitalityScore.ts all consume BANDS/bandFor).
export const BANDS = [
  { id: "excellent",   min: 85, max: 100, label: "Excellent",  color: "#FFC1CD" },
  { id: "good",        min: 70, max: 84,  label: "Good",       color: "#FFB0BD" },
  { id: "fair",        min: 50, max: 69,  label: "Fair",       color: "#D9A441" },
  { id: "challenging", min: 30, max: 49,  label: "Challenging", color: "#FF5C7A" },
  { id: "difficult",   min: 0,  max: 29,  label: "Difficult",  color: "#4B1F4F" },
  { id: "unscored",    min: -1, max: -1,  label: "—",          color: "#E8D5DA" },
] as const;
export type Band = (typeof BANDS)[number];

export function bandFor(score: number | null): Band {
  if (score == null) return BANDS[5];
  for (const b of BANDS) {
    if (b.id === "unscored") continue;
    if (score >= b.min && score <= b.max) return b;
  }
  return BANDS[5];
}

// Menopause Impact Score band table (v2.0, sum-based burden formula).
// Cutoffs are tuned for the new score distribution; colors mirror BANDS
// so the gauge / pill / heatmap palette stays consistent.
export const IMPACT_BANDS = [
  { id: "light",     min: 90, max: 100, label: "Light",     color: "#FFC1CD" },
  { id: "mild",      min: 75, max: 89,  label: "Mild",      color: "#FFB0BD" },
  { id: "moderate",  min: 55, max: 74,  label: "Moderate",  color: "#D9A441" },
  { id: "high",      min: 35, max: 54,  label: "High",      color: "#FF5C7A" },
  { id: "very-high", min: 0,  max: 34,  label: "Very High", color: "#4B1F4F" },
  { id: "unscored",  min: -1, max: -1,  label: "—",         color: "#E8D5DA" },
] as const;

export type ImpactBand = (typeof IMPACT_BANDS)[number];
export function impactBandFor(score: number | null): ImpactBand {
  if (score == null) return IMPACT_BANDS[5];
  for (const b of IMPACT_BANDS) {
    if (b.id === "unscored") continue;
    if (score >= b.min && score <= b.max) return b;
  }
  return IMPACT_BANDS[5];
}

// Headline copy used across the card, detail screen, and report PDF so
// burden language stays consistent. Never uses grading words like
// "good"/"excellent"/"difficult" — those have been retired in v2.0.
export function impactHeadline(score: number | null): string {
  if (score == null) return "Today's symptom load: — (—)";
  return `Today's symptom load: ${impactBandFor(score).label} (${score})`;
}
export function impactHeadlineForWindow(
  score: number | null,
  windowLabel: string,
): string {
  if (score == null) return `Symptom load (${windowLabel}): — (—)`;
  return `Symptom load (${windowLabel}): ${impactBandFor(score).label} (${score})`;
}

// One day's composite. NULL severities (= "not logged that day for that
// symptom") are EXCLUDED from the curved burden sum — we never punish
// users for what they didn't log. score=null means nothing was logged at
// all.
export type DailyScore = {
  dateKey: string;       // YYYY-MM-DD local-TZ
  score: number | null;  // 0..100, or null if no contributing symptoms
  loggedCount: number;   // how many of the 18 tracked symptoms contributed
};

/**
 * SERIES ORDERING CONVENTION — IMPORTANT.
 *
 * computeScoreSeries returns DailyScore[] in OLDEST → NEWEST order.
 * series[series.length-1] = today, series[0] = oldest day in the window.
 *
 * Every consumer (sparkline, 14d LineChart, recent vs prior split,
 * insight generator) assumes this orientation. If you ever flip it, the
 * delta sign, the "improving" copy, and the HRT reference line ALL silently
 * invert without throwing. The dev-only assertSeriesOldestFirst helper
 * (below) catches this in development.
 */
export function assertSeriesOldestFirst(series: DailyScore[]): void {
  if (typeof window === "undefined" || !(import.meta as any)?.env?.DEV) return;
  for (let i = 1; i < series.length; i++) {
    if (series[i - 1].dateKey > series[i].dateKey) {
      // eslint-disable-next-line no-console
      console.error("[menoScore] assertion failed: score series must be ordered oldest→newest", { prev: series[i - 1].dateKey, curr: series[i].dateKey });
      throw new Error("menoScore: series must be ordered oldest→newest");
    }
  }
}

// Pure: compute a single day's score from per-symptom severities (1..4 or
// null) keyed by symptom name. Hot flash severity should already be
// bucketed via hotFlashSeverityBucket() before being passed in.
//
// v2.0 — intensity-weighted symptom-burden model. Per logged symptom,
// severity maps via SEVERITY_POINTS (1→1, 2→2, 3→4, 4→6) so a single
// severe-4 symptom counts roughly six times a mild-1 symptom. The score
// is 100 minus the curved burden expressed as a fraction of
// REFERENCE_BURDEN (40 pts ≈ a reference-severe day), then clamped 0..100.
// REFERENCE_BURDEN is a calibration anchor — NOT the theoretical maximum
// (10 sev-4 symptoms = 60 pts, which clamps to 0). Higher score = lower
// symptom burden.
export function computeDailyScore(
  severitiesByName: Partial<Record<TrackedSymptom, number | null>>,
): { score: number | null; loggedCount: number } {
  let burden = 0, n = 0;
  for (const name of TRACKED_SYMPTOMS) {
    const v = severitiesByName[name];
    if (typeof v !== "number" || v <= 0) continue;
    burden += severityToPoints(v);
    n += 1;
  }
  if (n === 0) return { score: null, loggedCount: 0 };
  const score = Math.round(100 - (burden / REFERENCE_BURDEN) * 100);
  return { score: Math.max(0, Math.min(100, score)), loggedCount: n };
}

// Build oldest-first DailyScore[] for [todayKey - (days-1), todayKey].
// `logs` and `events` should already be fetched for the same window.
export function computeScoreSeries(
  todayKey: string,
  days: number,
  logs: DailyLogEntry[],
  events: SymptomEvent[],
): DailyScore[] {
  const start = parseLocalDateKey(todayKey);
  if (!start) return [];
  // Build oldest-first date keys.
  const dateKeys: string[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(start); d.setDate(start.getDate() - i);
    dateKeys.push(localDateKey(d));
  }
  // Bucket severity-tracked logs by date+name.
  const sevByDate: Record<string, Partial<Record<TrackedSymptom, number | null>>> = {};
  for (const r of logs) {
    if (r.severity == null || r.severity <= 0) continue;
    if (!(TRACKED_SYMPTOMS as readonly string[]).includes(r.symptom_name)) continue;
    (sevByDate[r.log_date] ||= {})[r.symptom_name as TrackedSymptom] = r.severity;
  }
  // Bucket hot flash event counts by local-TZ date.
  const hfCountByDate: Record<string, number> = {};
  for (const ev of events) {
    if (ev.symptom_name !== "Hot flashes") continue;
    const dk = localDateKey(new Date(ev.occurred_at));
    hfCountByDate[dk] = (hfCountByDate[dk] || 0) + 1;
  }
  const out = dateKeys.map((dk) => {
    const sev = { ...(sevByDate[dk] || {}) };
    // Hot flashes severity comes from event-count bucket, NOT daily_logs.
    const hfBucket = hotFlashSeverityBucket(hfCountByDate[dk] || 0);
    if (hfBucket != null) sev["Hot flashes"] = hfBucket;
    else delete sev["Hot flashes"];
    const { score, loggedCount } = computeDailyScore(sev);
    return { dateKey: dk, score, loggedCount };
  });
  assertSeriesOldestFirst(out);
  return out;
}

// One symptom currently "running high" — used by the in-app card/detail to
// honour the same honesty principle as the Symptom Intelligence Report's Clinical Flags:
// a positive band must never be presented without acknowledging a symptom
// that is currently severe. Threshold MATCHES the Symptom Intelligence Report's
// severe-days flag (`dd.lastSevere >= 10` in KnowMenoApp.tsx Clinical
// Flags): >= 10 severe days in the 30d window. Severe means severity >= 3
// for the 8 severity-tracked symptoms, or >= 10 hot-flash events in a day
// (matching hotFlashSeverityBucket's severe cutoff). Single shared
// definition so the card guard, the insight sentence, and the Doctor
// Report's Clinical Flags table can never disagree on count.
export type FlaggedSymptom = { name: TrackedSymptom; severeDays: number };

// Fetch + compute. React Query wrapper lives in the component.
export async function fetchMenoScore(userId: string, days = 30): Promise<{
  series: DailyScore[];
  today: DailyScore;
  todayLogsBySymptom: Partial<Record<TrackedSymptom, number | null>>;
  hfCountByDate: Record<string, number>;
  severityByDateBySymptom: Partial<Record<TrackedSymptom, Record<string, number | null>>>;
  flaggedSymptoms: FlaggedSymptom[];
}> {
  const todayKey = localDateKey();
  const start = parseLocalDateKey(todayKey);
  if (!start) {
    const empty: DailyScore = { dateKey: todayKey, score: null, loggedCount: 0 };
    return { series: [empty], today: empty, todayLogsBySymptom: {}, hfCountByDate: {}, severityByDateBySymptom: {}, flaggedSymptoms: [] };
  }
  const startKey = (() => {
    const d = new Date(start); d.setDate(start.getDate() - (days - 1));
    return localDateKey(d);
  })();
  const [logs, events] = await Promise.all([
    fetchLogsBetween(userId, startKey, todayKey),
    fetchHotFlashEventsBetween(userId, startKey, todayKey),
  ]);
  const series = computeScoreSeries(todayKey, days, logs, events);
  const today = series[series.length - 1] ?? { dateKey: todayKey, score: null, loggedCount: 0 };
  // For the detail screen score-factors list: today's per-symptom severities.
  const todayLogsBySymptom: Partial<Record<TrackedSymptom, number | null>> = {};
  for (const r of logs) {
    if (r.log_date !== todayKey) continue;
    if (!(TRACKED_SYMPTOMS as readonly string[]).includes(r.symptom_name)) continue;
    if (r.severity == null || r.severity <= 0) continue;
    todayLogsBySymptom[r.symptom_name as TrackedSymptom] = r.severity;
  }
  const hfCountByDate: Record<string, number> = {};
  for (const ev of events) {
    const dk = localDateKey(new Date(ev.occurred_at));
    hfCountByDate[dk] = (hfCountByDate[dk] || 0) + 1;
  }
  const hfBucket = hotFlashSeverityBucket(hfCountByDate[todayKey] || 0);
  if (hfBucket != null) todayLogsBySymptom["Hot flashes"] = hfBucket;

  // Per-symptom 14d severity for inline sparklines + expanded chart on the
  // detail screen. Derived from the already-fetched 30d logs array (no new
  // queries). Hot flashes uses bucketed severity from event counts so it
  // matches the score logic (single source of truth).
  const last14Keys = (() => {
    const out: string[] = [];
    const t = parseLocalDateKey(todayKey)!;
    for (let i = 13; i >= 0; i--) {
      const d = new Date(t); d.setDate(t.getDate() - i);
      out.push(localDateKey(d));
    }
    return out;
  })();
  const severityByDateBySymptom: Partial<Record<TrackedSymptom, Record<string, number | null>>> = {};
  for (const name of TRACKED_SYMPTOMS) {
    const m: Record<string, number | null> = {};
    for (const dk of last14Keys) m[dk] = null;
    severityByDateBySymptom[name] = m;
  }
  for (const r of logs) {
    if (!(TRACKED_SYMPTOMS as readonly string[]).includes(r.symptom_name)) continue;
    const m = severityByDateBySymptom[r.symptom_name as TrackedSymptom];
    if (m && r.log_date in m && r.severity != null && r.severity > 0) m[r.log_date] = r.severity;
  }
  const hfMap = severityByDateBySymptom["Hot flashes"]!;
  for (const dk of last14Keys) {
    const b = hotFlashSeverityBucket(hfCountByDate[dk] || 0);
    hfMap[dk] = b ?? null;
  }

  // Dev-only NULL invariant assertions. Catches sign flips early when the
  // computeScoreSeries / hotFlashSeverityBucket / computeDailyScore trio
  // gets edited without coordinated changes.
  if (typeof window !== "undefined" && (import.meta as any)?.env?.DEV) {
    // Edge case 1: only Hot flashes logged today → score MUST be 100 when 0 events.
    const onlyZeroHF = Object.keys(todayLogsBySymptom).length === 0
      && (hfCountByDate[todayKey] || 0) === 0
      && logs.some(l => l.log_date === todayKey);
    if (onlyZeroHF && today.score !== null) {
      console.error("[menoScore] invariant: today should be unscored when nothing logged", today);
    }
    // Edge case 2: nothing logged at all → score MUST be null.
    if (today.loggedCount === 0 && today.score !== null) {
      console.error("[menoScore] invariant: loggedCount=0 must yield score=null", today);
    }
  }

  // Flagged symptoms — same idea as the Symptom Intelligence Report's Clinical Flags:
  // surface anything currently "running high" so a positive band never
  // visually contradicts a severe symptom. Count severe days across the
  // full fetched window (`days`, default 30) so the threshold matches the
  // report's "severe days in last 30d" wording.
  const severeByName: Partial<Record<TrackedSymptom, number>> = {};
  for (const r of logs) {
    if (!(TRACKED_SYMPTOMS as readonly string[]).includes(r.symptom_name)) continue;
    if (r.symptom_name === "Hot flashes") continue; // HF handled via event counts below
    if (r.severity != null && r.severity >= 3) {
      const k = r.symptom_name as TrackedSymptom;
      severeByName[k] = (severeByName[k] || 0) + 1;
    }
  }
  // Hot flashes: a "severe day" = day with >= 10 events (bucket >= 3).
  let hfSevereDays = 0;
  for (const dk of Object.keys(hfCountByDate)) {
    if ((hfCountByDate[dk] || 0) >= 10) hfSevereDays += 1;
  }
  if (hfSevereDays > 0) severeByName["Hot flashes"] = hfSevereDays;
  const flaggedSymptoms: FlaggedSymptom[] = [];
  for (const name of TRACKED_SYMPTOMS) {
    const c = severeByName[name] || 0;
    if (c >= 10) flaggedSymptoms.push({ name, severeDays: c });
  }
  flaggedSymptoms.sort((a, b) => b.severeDays - a.severeDays);

  return { series, today, todayLogsBySymptom, hfCountByDate, severityByDateBySymptom, flaggedSymptoms };
}

// Long-window score history (default: 365 days) for the Annual Wellness
// Summary PDF. Same fetch+compute pattern as fetchMenoScore but returns
// only the oldest-first DailyScore[] series — no per-symptom breakdowns.
// computeScoreSeries is date-window-agnostic, so no new math is needed.
export async function fetchMenoScoreHistory(
  userId: string,
  days: number = 365,
): Promise<DailyScore[]> {
  const todayKey = localDateKey();
  const start = parseLocalDateKey(todayKey);
  if (!start) return [];
  const startKey = (() => {
    const d = new Date(start); d.setDate(start.getDate() - (days - 1));
    return localDateKey(d);
  })();
  const [logs, events] = await Promise.all([
    fetchLogsBetween(userId, startKey, todayKey),
    fetchHotFlashEventsBetween(userId, startKey, todayKey),
  ]);
  return computeScoreSeries(todayKey, days, logs, events);
}

// Top N symptoms by today's severity contribution. Used by the detail
// screen "score factors" list. Returns oldest tracked symptom first on ties
// (stable so the UI doesn't reorder between renders).
export function topDrivers(
  todayLogs: Partial<Record<TrackedSymptom, number | null>>,
  n = 5,
): { name: TrackedSymptom; severity: number }[] {
  const rows: { name: TrackedSymptom; severity: number }[] = [];
  for (const name of TRACKED_SYMPTOMS) {
    const v = todayLogs[name];
    if (typeof v !== "number" || v <= 0) continue;
    rows.push({ name, severity: v });
  }
  rows.sort((a, b) => b.severity - a.severity);
  return rows.slice(0, n);
}

// Impact-ranked driver list for the in-app Menopause Score detail screen.
// Ranks symptoms by their average severity across the fetched window (the
// biggest score-reducer first), so a chronically-severe symptom like
// Fatigue surfaces at the TOP even on a day the user didn't re-log it.
// Flagged symptoms (>=5 severe days in window) are always pulled to the
// front of the list, mirroring the Symptom Intelligence Report's honesty rule.
//
// `severity` returned is the symptom's current today-value when logged,
// else the rounded mean severity over the window (so the dot-row still
// reads meaningfully). HF severity is the today bucket if any events
// today, else the rounded mean of daily buckets across the window.
export function rankDriversByImpact(
  severityByDateBySymptom: Partial<Record<TrackedSymptom, Record<string, number | null>>>,
  todayLogs: Partial<Record<TrackedSymptom, number | null>>,
  flagged: FlaggedSymptom[],
): { name: TrackedSymptom; severity: number; isFlagged: boolean; meanSeverity: number }[] {
  const flaggedNames = new Set(flagged.map(f => f.name));
  const rows: { name: TrackedSymptom; severity: number; isFlagged: boolean; meanSeverity: number }[] = [];
  for (const name of TRACKED_SYMPTOMS) {
    const m = severityByDateBySymptom[name] || {};
    const vals = Object.values(m).filter((v): v is number => typeof v === "number" && v > 0);
    if (vals.length === 0) continue;
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    const today = todayLogs[name];
    const sev = typeof today === "number" && today > 0 ? today : Math.max(1, Math.min(4, Math.round(mean)));
    rows.push({ name, severity: sev, isFlagged: flaggedNames.has(name), meanSeverity: mean });
  }
  // Flagged first (by severeDays implied via meanSeverity desc), then by
  // mean severity desc. Stable on ties via TRACKED_SYMPTOMS order above.
  rows.sort((a, b) => {
    if (a.isFlagged !== b.isFlagged) return a.isFlagged ? -1 : 1;
    return b.meanSeverity - a.meanSeverity;
  });
  return rows;
}

// Insight one-liner. Decision tree (in priority order):
//   1. Today is unscored → encourage logging
//   2. <7d history → "still learning your patterns"
//   3. HRT started ≥45d ago AND delta(recent14 vs prior14) ≥ +10 → improving
//      (softened when any symptom is currently flagged severe)
//   4. HRT started ≥45d ago AND |delta| < 10 → steady post-HRT
//   5. delta ≥ +5 → trending up
//   6. delta ≤ -5 → trending down
//   7. else → steady
export function generateInsight(
  series: DailyScore[],
  hrtStartDate: Date | null,
  flagged: FlaggedSymptom[] = [],
): string {
  const today = series[series.length - 1];
  if (!today || today.score == null) {
    return "Log today's symptoms to see your score.";
  }
  const scored = series.filter(s => s.score != null);
  if (scored.length < 7) {
    return "Still learning your patterns — log a few more days.";
  }
  // Unified delta semantics — use the same rounded-window average math as
  // computeSmoothedHeadline so the insight sentence and the smoothed
  // headline always agree on sign + magnitude.
  const recent14 = scored.slice(-14).map(s => s.score!);
  const prior14 = scored.slice(-28, -14).map(s => s.score!);
  const roundedAvg = (xs: number[]) =>
    xs.length ? Math.round(xs.reduce((a, b) => a + b, 0) / xs.length) : null;
  const recentAvg = roundedAvg(recent14);
  const priorAvg = roundedAvg(prior14);
  const delta = recentAvg != null && priorAvg != null ? recentAvg - priorAvg : 0;
  const hrtDays = hrtStartDate
    ? Math.floor((Date.now() - hrtStartDate.getTime()) / 86400000)
    : 0;
  const topFlag = flagged[0]?.name?.toLowerCase() ?? null;
  if (hrtDays >= 45 && delta >= 10) {
    // Honesty guard: never say "it's working" cleanly while a symptom is
    // still flagged severe — hold both truths in one line. Copy must match
    // the flagged COUNT so the sentence never disagrees with the card's
    // "N symptoms still running high" guard label.
    if (topFlag) {
      if (flagged.length === 1) {
        return `Your score is up ${Math.round(delta)} points since starting HRT — most symptoms are easing, though ${topFlag} is still running high.`;
      }
      return `Your score is up ${Math.round(delta)} points since starting HRT — most symptoms are easing, though ${flagged.length} are still running high — ${topFlag} most of all.`;
    }
    return `Your score is up ${Math.round(delta)} points since starting HRT — it's working.`;
  }

  if (hrtDays >= 45 && Math.abs(delta) < 10) {
    return "Steady post-HRT. Keep tracking — your doctor will see the full picture.";
  }
  if (delta >= 5) return `Trending up — ${Math.round(delta)} points better than two weeks ago.`;
  if (delta <= -5) return `Down ${Math.round(Math.abs(delta))} points vs. two weeks ago — worth noting.`;
  return "Steady. Consistent tracking is what makes the Symptom Intelligence Report meaningful.";
}

// ---------------------------------------------------------------------------
// Smoothed headline (display-only).
//
// The raw daily score (computeDailyScore) is volatile — one sparse or rough
// day can swing the headline 30+ points. The Annual Wellness Summary and the
// per-day series must keep that raw signal, but the app score card and the
// Symptom Intelligence Report headline show a SMOOTHED value derived from the same series.
//
// Sparse-day guard: a day is only eligible for the rolling average if at
// least MIN_LOGGED_FOR_INCLUSION of the 9 tracked symptoms contributed.
// (≥ half; thin days drop out so incomplete logging can't produce an extreme
// headline.) Underlying daily formula is untouched.
// ---------------------------------------------------------------------------
export const SMOOTH_WINDOW_DAYS = 7;
// "A complete-enough day" — applies to BOTH the daily-headline incomplete-day
// guard (app card / detail gauge) AND the rolling-average day-inclusion
// (detail "recent average" stat, Symptom Intelligence Report headline). One definition,
// everywhere, so the daily score and the period average can never disagree
// on what counts as a real day. Annual Wellness Summary reads the raw
// history via fetchMenoScoreHistory and does NOT consult this constant.
export const MIN_LOGGED_FOR_INCLUSION = 3;

export type SmoothedHeadline = {
  score: number | null;
  prevScore: number | null;
  delta: number | null;
  trend: "improving" | "declining" | "steady" | "unknown";
  contributingDays: number;
  windowDays: number;
};

export function computeSmoothedHeadline(
  series: DailyScore[],
  windowDays: number = SMOOTH_WINDOW_DAYS,
): SmoothedHeadline {
  const eligible = (s: DailyScore): s is DailyScore & { score: number } =>
    s.score != null && s.loggedCount >= MIN_LOGGED_FOR_INCLUSION;
  const recent = series.slice(-windowDays).filter(eligible);
  const prior  = series.slice(-windowDays * 2, -windowDays).filter(eligible);
  const avg = (xs: { score: number }[]) =>
    xs.length ? Math.round(xs.reduce((a, b) => a + b.score, 0) / xs.length) : null;
  const score = avg(recent);
  const prevScore = avg(prior);
  const delta = score != null && prevScore != null ? score - prevScore : null;
  const trend: SmoothedHeadline["trend"] =
    delta == null ? "unknown"
    : delta >= 3 ? "improving"
    : delta <= -3 ? "declining"
    : "steady";
  return { score, prevScore, delta, trend, contributingDays: recent.length, windowDays };
}

// Dynamic insight for the smoothed headline. Never says "steady" when the
// smoothed value is actually moving, never claims absolute wellness — only
// references the user's own recent trajectory and the current band.
export function generateSmoothedInsight(
  smoothed: SmoothedHeadline,
  hrtStartDate: Date | null,
  flagged: FlaggedSymptom[] = [],
): string {
  if (smoothed.score == null) {
    return "Log a few more full days to see your recent score.";
  }
  const band = impactBandFor(smoothed.score);
  const bandLabel = band.label.toLowerCase();
  const hrtDays = hrtStartDate
    ? Math.floor((Date.now() - hrtStartDate.getTime()) / 86400000)
    : 0;
  const topFlag = flagged[0]?.name?.toLowerCase() ?? null;
  const isPositiveBand = band.id === "light" || band.id === "mild";
  const flagSuffix = isPositiveBand && topFlag
    ? (flagged.length === 1
        ? ` — ${topFlag} still running high.`
        : ` — ${flagged.length} symptoms still running high.`)
    : "";

  let core: string;
  if (smoothed.trend === "improving") {
    core = hrtDays >= 45
      ? `Up ${smoothed.delta} pts vs. the prior week — your post-HRT trend is moving up (${bandLabel} range).`
      : `Up ${smoothed.delta} pts vs. the prior week — currently in the ${bandLabel} range.`;
  } else if (smoothed.trend === "declining") {
    core = `Down ${Math.abs(smoothed.delta!)} pts vs. the prior week — currently in the ${bandLabel} range.`;
  } else if (smoothed.trend === "steady") {
    core = `Holding ${bandLabel} — within a few points of last week.`;
  } else {
    // Unknown — not enough prior-week data yet.
    core = `Currently in the ${bandLabel} range — keep logging to see your week-over-week trend.`;
  }
  return core + flagSuffix;
}



// Dynamic insight for the DAILY headline (today's raw score). Never says
// "steady" when today is actually different from yesterday, never claims
// absolute wellness, and never hardcodes a post-HRT message that could
// contradict a bad day. HRT context is appended as a secondary clause only
// — it never overrides today's reality.
export function generateDailyInsight(
  series: DailyScore[],
  hrtStartDate: Date | null,
  flagged: FlaggedSymptom[] = [],
): { text: string; hasFlag: boolean } {
  const today = series[series.length - 1];
  if (!today || today.score == null || today.loggedCount < MIN_LOGGED_FOR_INCLUSION) {
    return { text: `Today's check-in is incomplete — log at least ${MIN_LOGGED_FOR_INCLUSION} symptoms to see today's score.`, hasFlag: false };
  }

  const band = impactBandFor(today.score);
  const bandLabel = band.label.toLowerCase();
  // Find the most recent prior eligible day for a day-over-day delta.
  let yesterday: DailyScore | null = null;
  for (let i = series.length - 2; i >= 0; i--) {
    const s = series[i];
    if (s.score != null && s.loggedCount >= MIN_LOGGED_FOR_INCLUSION) { yesterday = s; break; }
  }
  const topFlag = flagged[0]?.name?.toLowerCase() ?? null;
  const isPositiveBand = band.id === "light" || band.id === "mild";
  const flagSuffix = isPositiveBand && topFlag
    ? (flagged.length === 1
        ? ` — ${topFlag} still running high.`
        : ` — ${flagged.length} symptoms still running high.`)
    : "";

  let core: string;
  if (!yesterday) {
    core = `Today is in the ${bandLabel} range — log tomorrow to see your day-over-day change.`;
  } else {
    const delta = today.score - (yesterday.score as number);
    if (delta >= 5) {
      core = `Better day than yesterday (+${delta} pts) — currently in the ${bandLabel} range.`;
    } else if (delta <= -5) {
      core = `Tougher day than yesterday (${delta} pts) — currently in the ${bandLabel} range.`;
    } else {
      core = `Today is in the ${bandLabel} range — within a few points of yesterday.`;
    }
  }
  // HRT note as secondary, never overrides today's reality.
  const hrtDays = hrtStartDate
    ? Math.floor((Date.now() - hrtStartDate.getTime()) / 86400000)
    : 0;
  const hrtSuffix = hrtDays >= 45 && !flagSuffix ? ` (Day ${hrtDays} on HRT.)` : "";
  return { text: core + flagSuffix + hrtSuffix, hasFlag: flagSuffix !== "" };
}


// Shared cell-color helper. Single source of truth for the heat-strip
// intensity buckets so the PDF heatmap and the detail-screen heatmap can
// never drift. Both Hot flashes (event-count bucket) and severity symptoms
// (1..4 bucket) go through here.
//
// EMPTY = no log that day (vs zero events for HF, which is also empty).
export function heatCellColor(opts: {
  symptomKey: string;
  severity: number | null | undefined;
  eventCount: number | null | undefined;
  baseColor: string;
  emptyColor?: string;
}): string {
  const empty = opts.emptyColor ?? "#FAF7F5";
  if (opts.symptomKey === "Hot flashes") {
    const c = opts.eventCount || 0;
    if (c === 0) return empty;
    if (c < 4)  return opts.baseColor + "55";
    if (c < 10) return opts.baseColor + "AA";
    if (c < 20) return opts.baseColor + "DD";
    return opts.baseColor;
  }
  const v = opts.severity;
  if (v == null) return empty;
  if (v <= 1) return opts.baseColor + "55";
  if (v === 2) return opts.baseColor + "AA";
  if (v === 3) return opts.baseColor + "DD";
  return opts.baseColor;
}
